import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { paymentProvidersSelector } from 'domain/paySettings';
import type { PaymentProviderRecord } from 'domain/paySettings/types';

import { PAYMENT_PROVIDERS_NAMES, PAYMENT_PROVIDERS_TITLES } from 'domain/paySettings/helpers';
import type { PaymentProviderName } from 'domain/paySettings/helpers';

import airwallexIcon from '../icons/airwallex.svg';
import leumiIcon from '../icons/leumiIcon.svg';
import raiffeisenIcon from '../icons/raiffeisenIcon.svg';
import rbsIcon from '../icons/rbsIcon.svg';
import checkbookIcon from '../icons/сheckbook.svg';
import openFinanceIcon from '../icons/openFinance.jpg';

export interface ProviderConfig {
  name: PaymentProviderName | string;
  title: string;
  icon: string;
  walletsLimit: number | null;
  isAvailableWallets: boolean;
  status: string;
  id: string | null;
}

const providersConfig: Partial<Record<PaymentProviderName, Omit<ProviderConfig, 'status' | 'id'>>> = {
  [PAYMENT_PROVIDERS_NAMES.airwallex]: {
    name: PAYMENT_PROVIDERS_NAMES.airwallex,
    title: PAYMENT_PROVIDERS_TITLES[PAYMENT_PROVIDERS_NAMES.airwallex],
    icon: airwallexIcon,
    walletsLimit: null,
    isAvailableWallets: true,
  },
  [PAYMENT_PROVIDERS_NAMES.leumi]: {
    name: PAYMENT_PROVIDERS_NAMES.leumi,
    title: PAYMENT_PROVIDERS_TITLES[PAYMENT_PROVIDERS_NAMES.leumi],
    icon: leumiIcon,
    walletsLimit: null,
    isAvailableWallets: true,
  },
  [PAYMENT_PROVIDERS_NAMES.raiffeisen]: {
    name: PAYMENT_PROVIDERS_NAMES.raiffeisen,
    title: PAYMENT_PROVIDERS_TITLES[PAYMENT_PROVIDERS_NAMES.raiffeisen],
    icon: raiffeisenIcon,
    walletsLimit: null,
    isAvailableWallets: true,
  },
  [PAYMENT_PROVIDERS_NAMES.rbs]: {
    name: PAYMENT_PROVIDERS_NAMES.rbs,
    title: PAYMENT_PROVIDERS_TITLES[PAYMENT_PROVIDERS_NAMES.rbs],
    icon: rbsIcon,
    walletsLimit: null,
    isAvailableWallets: true,
  },
  [PAYMENT_PROVIDERS_NAMES.checkbook]: {
    name: PAYMENT_PROVIDERS_NAMES.checkbook,
    title: PAYMENT_PROVIDERS_TITLES[PAYMENT_PROVIDERS_NAMES.checkbook],
    icon: checkbookIcon,
    walletsLimit: 5,
    isAvailableWallets: true,
  },
  [PAYMENT_PROVIDERS_NAMES.openFinance]: {
    name: PAYMENT_PROVIDERS_NAMES.openFinance,
    title: PAYMENT_PROVIDERS_TITLES[PAYMENT_PROVIDERS_NAMES.openFinance],
    icon: openFinanceIcon,
    walletsLimit: null,
    isAvailableWallets: false,
  },
};

const mapStateToProps = (state: unknown) => ({
  providers: paymentProvidersSelector(state),
});

function usePaymentProvidersConfig(): ProviderConfig[];
// eslint-disable-next-line no-redeclare
function usePaymentProvidersConfig(providerName: PaymentProviderName): ProviderConfig | null;
// eslint-disable-next-line no-redeclare
function usePaymentProvidersConfig(providerName?: PaymentProviderName): ProviderConfig[] | ProviderConfig | null {
  const { providers } = useSelector(mapStateToProps);

  const buildConfig = (provider: PaymentProviderRecord): ProviderConfig => {
    const baseConfig = providersConfig[provider.providerName as PaymentProviderName] ?? {
      name: '',
      title: '',
      icon: '',
      walletsLimit: null,
      isAvailableWallets: true,
    };
    return {
      ...baseConfig,
      name: baseConfig.name || provider.providerName,
      title: provider.displayName || baseConfig.title,
      status: provider.status,
      id: provider.id,
    };
  };

  return useMemo(() => {
    if (providerName) {
      const provider = providers.find((p: PaymentProviderRecord) => p.providerName === providerName);
      return provider ? buildConfig(provider) : null;
    }

    return providers.map(buildConfig).toArray();
  }, [providers, providerName]);
}

export default usePaymentProvidersConfig;
